import { useMemo, useState } from "react";
import { useOutletContext, Link } from "react-router-dom";
import { useTasks } from "../contexts/TaskContext";
import { useAuth } from "../contexts/AuthContext";
import TaskCard from "../components/TaskCard";
import TaskModal from "../components/TaskModal";
import VoiceCapture from "../components/VoiceCapture";
import { Plus, Sparkles, Zap } from "lucide-react";
import { cn } from "../lib/utils";
import { getWorkspace } from "../lib/workspaces";
import { useWorkLabel } from "../lib/workLabel";
import { preWarmMic } from "../lib/micPrewarm";

const COLUMNS = [
  { key: "todo", label: "To Do" },
  { key: "inprogress", label: "In Progress" },
  { key: "done", label: "Done" },
];

export default function Dashboard({ type }) {
  const { tasks, setStatus, loading } = useTasks();
  const { user } = useAuth();
  const { search } = useOutletContext() || { search: "" };
  const [editing, setEditing] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [voiceDefaults, setVoiceDefaults] = useState(null);
  const [dragOver, setDragOver] = useState(null);
  const workLabel = useWorkLabel({ isAuthed: !!user });

  const ws = getWorkspace(type);
  const WsIcon = ws.icon;
  const displayLabel = type === "work" && workLabel ? workLabel : ws.label;
  const displayFull = type === "work" && workLabel ? workLabel : ws.fullLabel;

  const filtered = useMemo(() => {
    let list = tasks.filter((t) => t.dashboard === type);
    if (search?.trim()) {
      const q = search.toLowerCase();
      list = list.filter(
        (t) =>
          t.title?.toLowerCase().includes(q) ||
          t.description?.toLowerCase().includes(q) ||
          t.notes?.toLowerCase().includes(q) ||
          (t.tags || []).some((tag) => tag.toLowerCase().includes(q))
      );
    }
    return list;
  }, [tasks, type, search]);

  const total = filtered.length;
  const doneCount = filtered.filter((t) => t.status === "done").length;
  const inProgressCount = filtered.filter((t) => t.status === "inprogress").length;
  const overdueCount = filtered.filter((t) => {
    if (!t.due_date || t.status === "done") return false;
    return new Date(t.due_date) < new Date(new Date().toDateString());
  }).length;

  const onDragStart = (e, task) => {
    e.dataTransfer.setData("text/plain", task.id);
  };

  const onDrop = async (e, columnKey) => {
    e.preventDefault();
    setDragOver(null);
    const taskId = e.dataTransfer.getData("text/plain");
    if (!taskId) return;
    const task = tasks.find((t) => t.id === taskId);
    if (!task || task.status === columnKey) return;
    await setStatus(taskId, columnKey);
  };

  return (
    <div
      data-testid={`workspace-${type}`}
      className="relative px-4 sm:px-8 py-8 max-w-[1600px] mx-auto transition-colors duration-500"
    >
      {/* Subtle ambient background glow tied to the workspace */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 -z-10 opacity-60"
        style={{
          background: `radial-gradient(900px 320px at 0% -10%, ${ws.color}1A 0%, transparent 60%)`,
        }}
      />

      {/* Header — prominent workspace identity */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-7 anim-fade-up">
        <div className="flex items-start gap-4">
          <div
            data-testid="dashboard-workspace-icon"
            className="w-14 h-14 rounded-2xl flex items-center justify-center shrink-0 transition-colors duration-300"
            style={{
              backgroundColor: `${ws.color}1A`,
              border: `1px solid ${ws.color}40`,
              color: ws.color,
            }}
          >
            <WsIcon size={26} strokeWidth={1.8} />
          </div>
          <div>
            <div
              className="text-[10px] uppercase tracking-[0.25em] mb-2 font-bold"
              style={{ color: ws.color }}
            >
              You are in
            </div>
            <h1
              data-testid="dashboard-workspace-title"
              className="font-display text-4xl sm:text-5xl font-black tracking-tight leading-none"
            >
              {displayFull}
            </h1>
            <p className="text-sm text-muted-foreground mt-2 max-w-md">{ws.description}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 self-start sm:self-auto">
          <Link
            to="/templates"
            data-testid="dashboard-templates-btn"
            className="inline-flex items-center gap-1.5 px-3 py-2.5 rounded-md text-sm font-medium border border-border hover:bg-secondary/60 transition"
          >
            <Sparkles size={14} strokeWidth={1.8} /> Templates
          </Link>
          <Link
            to="/capture?mode=voice"
            data-testid="dashboard-rapid-capture-btn"
            onClick={() => { try { preWarmMic(); } catch {} }}
            className="inline-flex items-center gap-1.5 px-3 py-2.5 rounded-md text-sm font-medium border transition-all duration-200"
            style={{
              borderColor: `${ws.color}50`,
              color: ws.color,
              backgroundColor: `${ws.color}08`,
            }}
            title="Rapid Capture — voice or text"
          >
            <Zap size={14} strokeWidth={2} /> Rapid Capture
          </Link>
          <VoiceCapture
            onParsed={({ parsed, transcript }) => {
              setVoiceDefaults({
                ...(parsed && Object.keys(parsed).length ? parsed : { title: transcript }),
                dashboard: parsed?.dashboard || type,
              });
              setCreateOpen(true);
            }}
            accentColor={ws.color}
            size="md"
            testIdPrefix="dashboard-voice"
          />
          <button
            onClick={() => {
              setVoiceDefaults(null);
              setCreateOpen(true);
            }}
            data-testid="dashboard-new-task-btn"
            style={{ backgroundColor: ws.color, color: "#fff" }}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md font-medium text-sm hover:opacity-90 active:scale-[0.98] transition-all duration-200"
          >
            <Plus size={15} strokeWidth={2} /> New {displayLabel.toLowerCase()} task
          </button>
        </div>
      </div>

      {/* Stats — accent picks up the workspace colour */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-7">
        <StatCard label="Total" value={total} accent={ws.color} />
        <StatCard label="In Progress" value={inProgressCount} accent={ws.color} dim />
        <StatCard label="Completed" value={doneCount} accent="#10b981" />
        <StatCard label="Overdue" value={overdueCount} accent="#ef4444" />
      </div>

      {/* School category quick-suggestions — always shown so users can keep
          adding pre-categorised tasks without hunting through menus. */}
      {ws.defaultCategories && !loading && (
        <div
          data-testid="school-categories-card"
          className="rounded-xl border border-dashed border-border bg-secondary/20 p-5 mb-6 anim-fade-up"
        >
          <div className="flex items-center gap-2 mb-3">
            <span
              className="text-[10px] uppercase tracking-[0.2em] font-bold"
              style={{ color: ws.color }}
            >
              Quick add
            </span>
            <span className="text-xs text-muted-foreground">
              — tap a category to start a task already tagged
            </span>
          </div>
          <div className="flex flex-wrap gap-2">
            {ws.defaultCategories.map((cat) => (
              <button
                key={cat}
                type="button"
                onClick={() => {
                  setVoiceDefaults({
                    title: "",
                    dashboard: type,
                    tags: [cat],
                  });
                  setCreateOpen(true);
                }}
                data-testid={`school-category-${cat.toLowerCase()}`}
                className="text-xs px-3 py-1.5 rounded-full border transition-all hover:scale-[1.02]"
                style={{
                  borderColor: `${ws.color}66`,
                  color: ws.color,
                  backgroundColor: `${ws.color}0D`,
                }}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Kanban */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {COLUMNS.map((col, idx) => {
          const items = filtered.filter((t) => t.status === col.key);
          const dotColor =
            col.key === "todo" ? "#94a3b8" : col.key === "inprogress" ? ws.color : "#10b981";
          const isDragOver = dragOver === col.key;
          return (
            <section
              key={col.key}
              data-testid={`kanban-col-${col.key}`}
              onDragOver={(e) => {
                e.preventDefault();
                setDragOver(col.key);
              }}
              onDragLeave={() => setDragOver(null)}
              onDrop={(e) => onDrop(e, col.key)}
              style={
                isDragOver
                  ? { backgroundColor: `${ws.color}1A`, borderColor: `${ws.color}66` }
                  : {}
              }
              className={cn(
                "kanban-col rounded-xl border border-border bg-secondary/30 p-4 min-h-[60vh] anim-fade-up transition-colors duration-200",
                `delay-${idx + 1}`
              )}
            >
              <header className="flex items-center justify-between mb-3 pb-3 border-b border-border">
                <div className="flex items-center gap-2">
                  <span
                    className="inline-block w-1.5 h-1.5 rounded-full"
                    style={{ backgroundColor: dotColor }}
                  />
                  <h3 className="font-display font-bold text-sm uppercase tracking-wider">
                    {col.label}
                  </h3>
                </div>
                <span className="text-xs font-mono text-muted-foreground">{items.length}</span>
              </header>

              <div className="space-y-2.5">
                {items.length === 0 && (
                  <div className="text-xs text-muted-foreground py-10 text-center border border-dashed border-border rounded-md">
                    {loading ? (
                      "Loading…"
                    ) : col.key === "todo" ? (
                      <div className="flex flex-col items-center gap-2">
                        <span>No tasks yet</span>
                        <Link
                          to="/templates"
                          data-testid={`kanban-empty-templates-link-${col.key}`}
                          className="inline-flex items-center gap-1 text-[11px] font-medium text-foreground hover:underline"
                          style={{ color: ws.color }}
                        >
                          <Sparkles size={11} /> Apply a template
                        </Link>
                      </div>
                    ) : (
                      "No tasks"
                    )}
                  </div>
                )}
                {items.map((t) => (
                  <TaskCard
                    key={t.id}
                    task={t}
                    onClick={(task) => setEditing(task)}
                    draggable
                    onDragStart={(e) => onDragStart(e, t)}
                    workspaceColor={ws.color}
                  />
                ))}
              </div>
            </section>
          );
        })}
      </div>

      <TaskModal
        open={!!editing}
        onClose={() => setEditing(null)}
        task={editing}
        defaultDashboard={type}
      />
      <TaskModal
        open={createOpen}
        onClose={() => {
          setCreateOpen(false);
          setVoiceDefaults(null);
        }}
        defaultDashboard={type}
        defaultValues={voiceDefaults}
      />

      {/* Mobile-only Rapid Capture FAB */}
      <Link
        to="/capture?mode=voice"
        data-testid="dashboard-rapid-capture-fab"
        onClick={() => { try { preWarmMic(); } catch {} }}
        aria-label="Rapid capture"
        className="sm:hidden fixed right-4 z-40 h-14 w-14 rounded-full flex items-center justify-center shadow-2xl active:scale-95 transition-transform"
        style={{
          background: `radial-gradient(circle at 30% 30%, ${ws.color}, #050505)`,
          color: "#fff",
          bottom: "max(1rem, calc(env(safe-area-inset-bottom) + 0.75rem))",
          boxShadow: `0 0 0 10px ${ws.color}1A, 0 12px 30px ${ws.color}55`,
        }}
      >
        <Zap size={22} strokeWidth={2} />
      </Link>
    </div>
  );
}

function StatCard({ label, value, accent, dim }) {
  return (
    <div
      className="rounded-lg border border-border bg-card p-4 transition-colors duration-300"
      style={dim ? {} : { borderColor: `${accent}30` }}
    >
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-muted-foreground mb-1.5">
        <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: accent }} />
        {label}
      </div>
      <div className="font-display text-3xl font-black tracking-tight" style={{ color: dim ? undefined : accent }}>
        {value}
      </div>
    </div>
  );
}
